import { useEffect, useRef } from 'react';

interface SEOConfig {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
}

function setMeta(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  const selector = isProperty
    ? `meta[property="${name}"]`
    : `meta[name="${name}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function removeMeta(name: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  const selector = isProperty
    ? `meta[property="${name}"]`
    : `meta[name="${name}"]`;
  const el = document.head.querySelector<HTMLMetaElement>(selector);
  if (el) {
    el.remove();
  }
}

function injectJSONLD(id: string, data: object) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data, null, 0);
}

export default function useSEO(config: SEOConfig) {
  const {
    title,
    description,
    canonicalUrl = window.location.href,
    ogImage,
    ogType = 'website',
    publishedTime,
    modifiedTime,
    tags,
  } = config;

  const prevCleanup = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Cleanup previous page's dynamic meta
    if (prevCleanup.current) {
      prevCleanup.current();
    }

    const createdSelectors: string[] = [];

    // Title
    document.title = title;

    // Description
    setMeta('description', description);

    // Canonical
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:locale', 'zh_CN', true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
      setMeta('og:image:width', '1200', true);
      setMeta('og:image:height', '630', true);
    }

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) {
      setMeta('twitter:image', ogImage);
    }

    // Article specific
    if (ogType === 'article' && publishedTime) {
      setMeta('article:published_time', publishedTime, true);
      if (modifiedTime) {
        setMeta('article:modified_time', modifiedTime, true);
      }
      if (tags && tags.length > 0) {
        setMeta('article:tag', tags.join(','), true);
      }
    }

    // Cleanup function
    prevCleanup.current = () => {
      // Clean article-specific metas
      if (ogType === 'article') {
        removeMeta('article:published_time', true);
        removeMeta('article:modified_time', true);
        removeMeta('article:tag', true);
      }
    };

    return () => {
      if (prevCleanup.current) {
        prevCleanup.current();
        prevCleanup.current = null;
      }
    };
  }, [title, description, canonicalUrl, ogImage, ogType, publishedTime, modifiedTime, tags]);
}

export { injectJSONLD, setMeta };